import { browser } from '$app/environment';

// Cache for localized prices to avoid repeated API calls
let priceCache = new Map();
let cacheExpiry = null;
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

// Global pricing request promise for coordinating all pricing requests
let global_pricing_promise = null;

/**
 * Extract all unique price IDs from courses, worksheets, and bundles
 * @param {Course[]} courses
 * @param {Worksheet[]} worksheets
 * @param {Bundle[]} bundles
 * @returns {string[]} Array of unique price IDs
 */
function extract_price_ids(courses, worksheets, bundles) {
    const price_ids = new Set();

    // Extract from courses
    courses.forEach((course) => {
        if (course.price?.price_id) {
            price_ids.add(course.price.price_id);
        }
        if (course.price_gift?.price_id) {
            price_ids.add(course.price_gift.price_id);
        }
    });

    // Extract from worksheets
    worksheets.forEach((worksheet) => {
        if (worksheet.price?.price_id) {
            price_ids.add(worksheet.price.price_id);
        }
    });

    // Extract from bundles
    bundles.forEach((bundle) => {
        if (bundle.price?.price_id) {
            price_ids.add(bundle.price.price_id);
        }
        if (bundle.price_gift?.price_id) {
            price_ids.add(bundle.price_gift.price_id);
        }
        // member item prices, so the bundle's item list + total localize too
        bundle.items?.forEach((item) => {
            // @ts-ignore - extra items carry no price_id; optional chaining guards it
            if (item.price?.price_id) {
                // @ts-ignore
                price_ids.add(item.price.price_id);
            }
        });
    });

    return Array.from(price_ids);
}

/**
 * Get customer data from localStorage for accurate pricing
 * @returns {object} Customer data for Paddle API
 */
function get_customer_data() {
    if (!browser) return {};

    try {
        const customer_id = localStorage.getItem('ctm_id');
        const address_id = localStorage.getItem('add_id');
        const customer_email = localStorage.getItem('ctm_email');

        // Paddle requires both customer_id and address_id for customer-based pricing
        if (customer_id && address_id) {
            return { customer_id, address_id };
        }

        // If we don't have both, fall back to IP-based pricing
        return {};
    } catch {
        return {};
    }
}

/**
 * Fetch localized prices from Paddle API
 * @param {string[]} price_ids
 * @returns {Promise<Map<string, {price: number, currency: string}>>}
 */
async function fetch_localized_prices(price_ids) {
    if (price_ids.length === 0) {
        return new Map();
    }

    try {
        const customer_data = get_customer_data();

        const response = await fetch('/api/paddle/pricing-preview', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                price_ids,
                ...customer_data
            })
        });

        if (!response.ok) {
            console.warn('Pricing API failed, using USD fallback');
            return new Map();
        }

        const result = await response.json();

        // Convert response to Map for easy lookup
        const price_map = new Map();
        result.prices?.forEach(({ price_id, total, currency_code }) => {
            price_map.set(price_id, {
                price: total / 100, // Convert from cents to dollars
                currency: currency_code
            });
        });

        return price_map;
    } catch (error) {
        console.warn('Error fetching localized prices:', error);
        return new Map();
    }
}

/**
 * Check if cache is valid and not expired
 * @returns {boolean}
 */
function is_cache_valid() {
    return cacheExpiry && Date.now() < cacheExpiry && priceCache.size > 0;
}

/**
 * Update cache with new price data
 * @param {Map} price_data
 */
function update_cache(price_data) {
    priceCache = price_data;
    cacheExpiry = Date.now() + CACHE_DURATION;
}

/**
 * Patch price objects with localized pricing data
 * @param {object} price_obj
 * @param {Map} localized_prices
 */
function patch_price_object(price_obj, localized_prices) {
    if (!price_obj?.price_id) return;

    const localized_data = localized_prices.get(price_obj.price_id);
    if (localized_data) {
        price_obj.price = localized_data.price;
        price_obj.price_fx = localized_data.currency;
    }
}

/**
 * Localize a bundle's own price, its gift price, and each member item's price,
 * then recompute the item total and discount in the localized currency so the
 * struck-through price, the "total value" line, and the discount badge all agree.
 * Safe to call on non-bundles (no items) — it just localizes price/price_gift.
 * @param {object} bundle
 * @param {Map} localized_prices
 */
function patch_bundle(bundle, localized_prices) {
    const base_price = bundle.price?.price || 0;

    patch_price_object(bundle.price, localized_prices);
    patch_price_object(bundle.price_gift, localized_prices);

    if (!bundle?.items?.length) return;

    bundle.items.forEach((item) =>
        patch_price_object(item.price, localized_prices)
    );

    const fx = bundle.price?.price_fx || bundle.price_items?.price_fx || 'USD';

    // Extra items have no Paddle price to localize, so convert them at the
    // bundle's own rate (FX + tax), or their USD number would be summed as
    // if it were the local currency. Already-converted extras are skipped.
    const rate = base_price > 0 ? (bundle.price?.price || 0) / base_price : 1;
    bundle.items.forEach((item) => {
        if (!item.price || item.price.price_id || item.price.price_fx === fx) {
            return;
        }
        item.price.price = Math.round(item.price.price * rate * 100) / 100;
        item.price.price_fx = fx;
    });
    const items_price = bundle.items.reduce(
        (acc, i) => acc + (i.price?.price || 0),
        0
    );
    bundle.price_items = { price: items_price, price_fx: fx };

    const bundle_price = bundle.price?.price || 0;
    if (items_price > 0 && bundle_price > 0 && bundle_price < items_price) {
        bundle.discount = Math.round(
            ((items_price - bundle_price) / items_price) * 100
        );
        bundle.discount_percent = `&minus;${bundle.discount}%`;
    }
}

/**
 * Patch a single product with localized prices
 * @param {Course|Worksheet|Bundle} product
 * @returns {Promise<Course|Worksheet|Bundle>}
 */
export async function patch_single_product_prices(product) {
    // Only run in browser
    if (!browser) {
        return product;
    }

    try {
        // Extract price IDs from single product
        const price_ids = [];
        if (product.price?.price_id) {
            price_ids.push(product.price.price_id);
        }

        if (product.price_gift?.price_id) {
            // @ts-ignore
            price_ids.push(product.price_gift.price_id);
        }

        // bundle member items, so their prices + the bundle total localize too
        // @ts-ignore - items only exists on bundles
        product.items?.forEach((item) => {
            if (item.price?.price_id) {
                price_ids.push(item.price.price_id);
            }
        });

        if (price_ids.length === 0) {
            return product;
        }

        let localized_prices;

        // Check cache first - if global request already populated it, use it
        if (is_cache_valid()) {
            localized_prices = priceCache;
        } else if (global_pricing_promise) {
            // Wait for the global pricing request to complete
            try {
                await global_pricing_promise;
                // Check cache again after global request completes
                if (is_cache_valid()) {
                    localized_prices = priceCache;
                } else {
                    // Global request didn't populate our specific prices, make individual request
                    localized_prices = await fetch_localized_prices(price_ids);
                    update_cache(localized_prices);
                }
            } catch {
                // Global request failed, make individual request
                localized_prices = await fetch_localized_prices(price_ids);
                update_cache(localized_prices);
            }
        } else {
            // No global request and no cache, make individual request
            localized_prices = await fetch_localized_prices(price_ids);
            update_cache(localized_prices);
        }

        // If no localized data, return original product
        if (localized_prices.size === 0) {
            return product;
        }

        // Patch price objects (and, for bundles, member items + recomputed totals)
        patch_bundle(product, localized_prices);

        return product;
    } catch (error) {
        console.warn('Error in patch_single_product_prices:', error);
        return product;
    }
}

/**
 * Patch all products with localized prices using a single API call
 * @param {Course[]} courses
 * @param {Worksheet[]} worksheets
 * @param {Bundle[]} bundles
 * @returns {Promise<[Course[], Worksheet[], Bundle[]]>}
 */
export async function patch_localized_prices(courses, worksheets, bundles) {
    // Only run in browser
    if (!browser) {
        return [courses, worksheets, bundles];
    }

    // Return existing promise if one is already running
    if (global_pricing_promise) {
        return global_pricing_promise;
    }

    // Create and store the global promise
    global_pricing_promise = (async () => {
        try {
            let localized_prices;

            // Check cache first
            if (is_cache_valid()) {
                localized_prices = priceCache;
            } else {
                // Extract all price IDs in one pass
                const price_ids = extract_price_ids(courses, worksheets, bundles);

                // Fetch localized prices with single API call
                localized_prices = await fetch_localized_prices(price_ids);

                // Update cache
                update_cache(localized_prices);
            }

            // If no localized data, return original arrays
            if (localized_prices.size === 0) {
                return /** @type {[Course[], Worksheet[], Bundle[]]} */ ([courses, worksheets, bundles]);
            }

            // Patch courses
            courses.forEach((course) => {
                patch_price_object(course.price, localized_prices);
                patch_price_object(course.price_gift, localized_prices);
            });

            // Patch worksheets
            worksheets.forEach((worksheet) => {
                patch_price_object(worksheet.price, localized_prices);
            });

            // Patch bundles (price, gift, member items + recomputed totals)
            bundles.forEach((bundle) => {
                patch_bundle(bundle, localized_prices);
            });

            return /** @type {[Course[], Worksheet[], Bundle[]]} */ ([courses, worksheets, bundles]);
        } catch (error) {
            console.warn('Error in patch_localized_prices:', error);
            return /** @type {[Course[], Worksheet[], Bundle[]]} */ ([courses, worksheets, bundles]);
        } finally {
            global_pricing_promise = null;
        }
         
    })();

    return global_pricing_promise;
}
